import { useEffect, useState } from "react";
import { Check, Eye, X } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { QuestionPreview } from "../components/QuestionPreview";
import { ImageUpload } from "../components/ImageUpload";
import { MarkingGuideInput } from "../components/MarkingGuideInput";
import { loadKatex } from "../components/MathText";
import { guideProblem } from "../lib/markingGuide";
import { letterAfter, letterBefore, sortedOptions, withOption } from "../lib/options";
import {
  fetchSubjects,
  fetchTopics,
  fetchSubtopics,
  fetchQuestion,
  createQuestion,
  updateQuestion,
  type Topic,
  type SubTopic,
} from "../api/worker";

const QUESTION_TYPES = [
  { value: "mcq", label: "Multiple Choice (MCQ)" },
  { value: "essay", label: "Essay / Theory" },
  { value: "true_false", label: "True / False" },
  { value: "image_multiple_mcq", label: "Image + MCQ" },
  { value: "multiple_mcq", label: "Multiple MCQ (shared passage)" },
];

const STATUSES = ["active", "draft", "under_review", "archived"];
const SOURCES = [
  "jamb_past_question",
  "waec_past_question",
  "jamb_ai_generated",
  "waec_ai_generated",
];

export function QuestionEditorScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [subtopics, setSubtopics] = useState<SubTopic[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState("mcq");
  const [subjectId, setSubjectId] = useState("");
  const [topicId, setTopicId] = useState("");
  const [subTopicId, setSubTopicId] = useState("");
  const [options, setOptions] = useState<Record<string, string>>({ A: "", B: "", C: "", D: "" });
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [difficultyLevel, setDifficultyLevel] = useState<number | "">("");
  const [examType, setExamType] = useState("");
  const [examYear, setExamYear] = useState<number | "">("");
  const [questionSource, setQuestionSource] = useState("");
  const [status, setStatus] = useState("active");
  const [imageUrl, setImageUrl] = useState("");
  const [passageContent, setPassageContent] = useState("");
  const [previewMode, setPreviewMode] = useState<"preview" | "correct" | "wrong" | null>(null);

  // Load subjects
  useEffect(() => {
    fetchSubjects()
      .then((r) => setSubjects(r.subjects))
      .catch(() => {});
  }, []);

  // Load topics when subject changes
  useEffect(() => {
    if (!subjectId) {
      setTopics([]);
      return;
    }
    fetchTopics(subjectId)
      .then((r) => setTopics(r.topics))
      .catch(() => {});
  }, [subjectId]);

  // Load subtopics when topic changes
  useEffect(() => {
    if (!topicId) {
      setSubtopics([]);
      return;
    }
    fetchSubtopics(topicId)
      .then((r) => setSubtopics(r.subtopics))
      .catch(() => {});
  }, [topicId]);

  // Load existing question for edit
  useEffect(() => {
    if (!id) return;
    fetchQuestion(id)
      .then((q) => {
        setQuestionText(q.questionText);
        setQuestionType(q.questionType);
        setSubjectId(q.subject?.id ?? "");
        setTopicId(q.topic?.id ?? "");
        setSubTopicId(q.subTopic?.id ?? "");
        // In letter order: an extracted question's options can arrive in any.
        setOptions(sortedOptions(q.options ?? { A: "", B: "", C: "", D: "" }));
        setCorrectAnswer(q.correctAnswer ?? "");
        setExplanation(q.explanation ?? "");
        setDifficultyLevel(q.difficultyLevel ?? "");
        setExamType(q.examType ?? "");
        setExamYear(q.examYear ?? "");
        setQuestionSource(q.questionSource ?? "");
        setStatus(q.status);
        setImageUrl(q.imageUrl ?? "");
        setPassageContent(q.passage?.content ?? "");
      })
      .catch(() => {});
  }, [id]);

  const needsImage = questionType === "image_multiple_mcq";
  const isObjective = ["mcq", "true_false", "image_multiple_mcq", "multiple_mcq"].includes(
    questionType,
  );
  // Theory: the correct answer field holds the marking guide.
  const isTheory = questionType === "essay";

  const save = async () => {
    if (needsImage && !imageUrl) {
      setError("Upload the image this question needs.");
      return;
    }
    if (!questionText.trim() || !subjectId) {
      setError("Question text and subject are required.");
      return;
    }
    if (isTheory) {
      // Every equation must render, or students would see raw LaTeX.
      const problem = guideProblem(correctAnswer, await loadKatex());
      if (problem) {
        setError(problem);
        return;
      }
    }
    setSaving(true);
    setError(null);

    const body: Record<string, unknown> = {
      questionText: questionText.trim(),
      questionType,
      subjectId,
      topicId: topicId || undefined,
      subTopicId: subTopicId || undefined,
      explanation: explanation.trim() || undefined,
      difficultyLevel: difficultyLevel || undefined,
      examType: examType || undefined,
      examYear: examYear || undefined,
      questionSource: questionSource || undefined,
      status,
    };

    if (isObjective) {
      body.options = options;
      body.correctAnswer = correctAnswer || undefined;
    }
    // Sent as null on an edit, so clearing the guide actually clears it.
    if (isTheory) body.correctAnswer = correctAnswer.trim() || (isEdit ? null : undefined);
    // Sent even when empty on an edit, so removing an image actually removes it.
    if (imageUrl) body.imageUrl = imageUrl;
    else if (isEdit) body.imageUrl = null;
    if (passageContent.trim()) body.passageContent = passageContent.trim();

    try {
      if (isEdit && id) {
        await updateQuestion(id, body);
      } else {
        await createQuestion(body);
      }
      navigate("/questions");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // By letter, not by count: counting gave {B} a second "B" (the existing
  // one, emptied) instead of a new option. Extracted questions can start past
  // A, or have a gap, so the letters themselves decide.
  const before = letterBefore(options);
  const after = letterAfter(options);
  const addOption = (letter: string | null) => {
    if (letter) setOptions(withOption(options, letter));
  };

  const removeOption = (key: string) => {
    const next = { ...options };
    delete next[key];
    setOptions(next);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="text-[18px] font-bold lg:text-[22px]">
        {isEdit ? "Edit Question" : "New Question"}
      </h1>

      {error ? (
        <p className="mt-4 rounded-lg bg-negative/10 px-3 py-2 text-[13px] text-negative">
          {error}
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-5">
        {/* Type */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Question Type
          </label>
          <select
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Subject → Topic → Subtopic cascade */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Subject *
            </label>
            <select
              value={subjectId}
              onChange={(e) => {
                setSubjectId(e.target.value);
                setTopicId("");
                setSubTopicId("");
              }}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
            >
              <option value="">Select…</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Topic
            </label>
            <select
              value={topicId}
              onChange={(e) => {
                setTopicId(e.target.value);
                setSubTopicId("");
              }}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
              disabled={!subjectId}
            >
              <option value="">Select…</option>
              {topics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Subtopic
            </label>
            <select
              value={subTopicId}
              onChange={(e) => setSubTopicId(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
              disabled={!topicId}
            >
              <option value="">Select…</option>
              {subtopics.map((st) => (
                <option key={st.id} value={st.id}>
                  {st.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Passage (for comprehension) */}
        {["mcq", "multiple_mcq", "essay"].includes(questionType) ? (
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Passage / Instruction (optional)
            </label>
            <textarea
              rows={4}
              value={passageContent}
              onChange={(e) => setPassageContent(e.target.value)}
              placeholder="Enter comprehension passage or instruction text…"
              className="w-full resize-y rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none placeholder:text-text-muted"
            />
          </div>
        ) : null}

        {/* Image: uploaded to storage here, its link saved with the question.
            Required for image questions; any other question can carry one too. */}
        <ImageUpload
          label={needsImage ? "Image" : "Image (optional)"}
          required={needsImage}
          value={imageUrl}
          onChange={setImageUrl}
        />

        {/* Question text */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Question Text *
          </label>
          <textarea
            rows={4}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Enter the question…"
            className="w-full resize-y rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none placeholder:text-text-muted"
          />
        </div>

        {/* Options (MCQ types only) */}
        {isObjective ? (
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Options
            </label>
            {/* Only when the first option isn't A: there's room above it. */}
            {before && Object.keys(options).length ? (
              <button
                type="button"
                onClick={() => addOption(before)}
                className="mb-2 text-[12px] font-semibold text-accent hover:underline"
              >
                + Add option {before} above
              </button>
            ) : null}
            <div className="flex flex-col gap-2">
              {Object.entries(options).map(([key, text]) => (
                <div key={key} className="flex items-center gap-2">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[13px] font-bold ${
                      correctAnswer === key
                        ? "bg-positive/20 text-positive"
                        : "bg-surface-high text-text-muted"
                    }`}
                  >
                    {key}
                  </span>
                  <input
                    type="text"
                    value={text}
                    onChange={(e) => setOptions({ ...options, [key]: e.target.value })}
                    placeholder={`Option ${key}`}
                    className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-[14px] text-text outline-none placeholder:text-text-muted"
                  />
                  <button
                    type="button"
                    onClick={() => setCorrectAnswer(key)}
                    title="Mark as correct"
                    className={`rounded-lg px-2 py-1.5 text-[11px] font-semibold ${
                      correctAnswer === key
                        ? "bg-positive/20 text-positive"
                        : "bg-surface-high text-text-muted hover:text-positive"
                    }`}
                    aria-label="Mark as correct answer"
                  >
                    <Check size={14} aria-hidden="true" />
                  </button>
                  {Object.keys(options).length > 2 ? (
                    <button
                      type="button"
                      onClick={() => removeOption(key)}
                      className="text-text-muted hover:text-negative"
                      aria-label="Remove option"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            {after ? (
              <button
                type="button"
                onClick={() => addOption(after)}
                className="mt-2 text-[12px] font-semibold text-accent hover:underline"
              >
                + Add option {after}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* Marking guide (theory only) */}
        {isTheory ? <MarkingGuideInput value={correctAnswer} onChange={setCorrectAnswer} /> : null}

        {/* Explanation */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Explanation
          </label>
          <textarea
            rows={3}
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            placeholder="Why is this the correct answer?"
            className="w-full resize-y rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none placeholder:text-text-muted"
          />
        </div>

        {/* Metadata row */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Difficulty (1-5)
            </label>
            <input
              type="number"
              min={1}
              max={5}
              value={difficultyLevel}
              onChange={(e) => setDifficultyLevel(e.target.value ? Number(e.target.value) : "")}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Exam Type
            </label>
            <select
              value={examType}
              onChange={(e) => setExamType(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
            >
              <option value="">—</option>
              <option value="JAMB">JAMB</option>
              <option value="WAEC">WAEC</option>
              <option value="BOTH">Both</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Exam Year
            </label>
            <input
              type="number"
              min={2000}
              max={2030}
              value={examYear}
              onChange={(e) => setExamYear(e.target.value ? Number(e.target.value) : "")}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Source
            </label>
            <select
              value={questionSource}
              onChange={(e) => setQuestionSource(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
            >
              <option value="">—</option>
              {SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2.5 text-[14px] text-text outline-none"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className="rounded-lg bg-accent px-6 py-2.5 text-[14px] font-semibold text-bg hover:bg-accent-light disabled:opacity-50"
          >
            {saving ? "Saving…" : isEdit ? "Update Question" : "Create Question"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/questions")}
            className="rounded-lg border border-border bg-surface px-6 py-2.5 text-[14px] text-text-muted hover:text-text"
          >
            Cancel
          </button>

          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() => setPreviewMode(previewMode === "preview" ? null : "preview")}
              className={`rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                previewMode === "preview"
                  ? "bg-accent/15 text-accent-light"
                  : "border border-border bg-surface text-text-muted hover:text-text"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Eye size={14} aria-hidden="true" /> Student view
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode(previewMode === "correct" ? null : "correct")}
              className={`rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                previewMode === "correct"
                  ? "bg-positive/15 text-positive"
                  : "border border-border bg-surface text-text-muted hover:text-text"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <Check size={14} aria-hidden="true" /> Correct answer
              </span>
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode(previewMode === "wrong" ? null : "wrong")}
              className={`rounded-lg px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                previewMode === "wrong"
                  ? "bg-negative/15 text-negative"
                  : "border border-border bg-surface text-text-muted hover:text-text"
              }`}
            >
              <span className="inline-flex items-center gap-1.5">
                <X size={14} aria-hidden="true" /> Wrong answer
              </span>
            </button>
          </div>
        </div>

        {/* Live Preview */}
        {previewMode ? (
          <div className="mt-6">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              {previewMode === "preview"
                ? "Student View — before answering"
                : previewMode === "correct"
                  ? "Marking — correct answer selected"
                  : "Marking — wrong answer selected"}
            </p>
            <QuestionPreview
              questionText={questionText}
              questionType={questionType}
              options={isObjective ? options : null}
              correctAnswer={correctAnswer || null}
              explanation={explanation || null}
              imageUrl={imageUrl || null}
              passageContent={passageContent || null}
              subject={subjects.find((s) => s.id === subjectId)?.name ?? ""}
              topic={topics.find((t) => t.id === topicId)?.name ?? ""}
              mode={previewMode}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
